import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuInfo, LuLoaderCircle, LuRotateCcw, LuSave, LuUserPen, LuUserPlus, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import MultiSelect from '../../../components/form/MultiSelect';
import SearchSelect from '../../../components/form/SearchSelect';
import { Field, SelectInput, TextArea } from '../../../components/form/FormControls';
import { useTranslation } from '../../../i18n/LanguageContext';
import { dedupeCenters, optionMatchesAnyCenterId } from '../../../utils/centerDedupe';
import { ROLE_OPTIONS, centerDedupeLabel, formatLeftDate, roleLabel } from './workerOptions';

/** Single choice shown as pills; pressing the chosen one again clears it (as the old checkboxes did). */
const ChoiceChips = ({ id, options, value, onChange }) => (
  <div id={id} role="radiogroup" className="tw-flex tw-flex-wrap tw-gap-2">
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(selected ? '' : option.value)}
          className={`tw-inline-flex tw-h-10 tw-items-center tw-rounded-full tw-border tw-px-4 tw-text-sm tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
            selected
              ? 'tw-border-teal-500/40 tw-bg-teal-500/15 tw-text-teal-800 dark:tw-border-teal-400/30 dark:tw-text-teal-200'
              : 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-600 hover:tw-bg-white hover:tw-text-slate-900 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

ChoiceChips.propTypes = {
  id: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

const CHIP = 'tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-full tw-py-1 tw-pl-2.5 tw-pr-1 tw-text-xs tw-font-semibold';

/**
 * "Projeye Çalışan Ekle" (`mode="add"`) and "Çalışan Bilgilerini Düzenle".
 * The state lives in WorkersModal (as before); this is the form for it.
 * In edit mode, removing a centre marks it as left (its history, files and
 * rates stay) and it can be restored before saving.
 */
const WorkerFormModal = ({
  mode,
  worker,
  onClose,
  onSubmit,
  loading,
  availableEmployees,
  centers,
  selectedEmployee,
  setSelectedEmployee,
  selectedRole,
  setSelectedRole,
  selectedCenters,
  setSelectedCenters,
  archivedCenters,
  setArchivedCenters,
  selectedInfo,
  setSelectedInfo,
  selectedWorkerType,
  setSelectedWorkerType,
  selectedBlindStatus,
  setSelectedBlindStatus,
  selectedStatus,
  setSelectedStatus,
  hasManagerWarning,
}) => {
  const { t, language, locale } = useTranslation();
  const isEdit = mode === 'edit';
  const label = (key, vars) => t(`projectManagement.workerForm.${key}`, vars);
  const chipsRef = useRef(null);
  const chipCountRef = useRef(selectedCenters.length);

  // Yeni merkez eklendiğinde seçilenler listesi en alta kayar
  useEffect(() => {
    if (selectedCenters.length > chipCountRef.current && chipsRef.current) {
      chipsRef.current.scrollTop = chipsRef.current.scrollHeight;
    }
    chipCountRef.current = selectedCenters.length;
  }, [selectedCenters.length]);

  const employeeOptions = useMemo(() => availableEmployees
    .filter((emp) => emp.Active === 1 || emp.Active === undefined)
    .sort((a, b) => `${a.eName} ${a.eSurname}`.localeCompare(`${b.eName} ${b.eSurname}`))
    .map((emp) => ({ value: emp.id, label: `${emp.eName || ''} ${emp.eSurname || ''}`.trim() })), [availableEmployees]);

  // Aynı hastane için birden çok Centers dokümanı var: tek satırda birleşir;
  // satırın temsil ettiği her id seçili sayılır, seçimi kaldırmak hepsini temizler
  const sortedCenters = useMemo(() => [...centers].sort((a, b) => (a.Adı || '').localeCompare(b.Adı || '', 'tr')), [centers]);
  const centerOptions = useMemo(() => dedupeCenters(sortedCenters, centerDedupeLabel).options, [sortedCenters]);

  const toggleCenter = (option) => {
    const groupIds = option.duplicateIds || [option.id];
    // Seçerken asıl id saklanır; kaldırırken tüm kopyalar düşer
    setSelectedCenters(optionMatchesAnyCenterId(option, selectedCenters)
      ? selectedCenters.filter((id) => !groupIds.includes(id))
      : [...selectedCenters, option.id]);
  };

  const toggleRole = (option) => {
    setSelectedRole(selectedRole.includes(option.name)
      ? selectedRole.filter((name) => name !== option.name)
      : [...selectedRole, option.name]);
  };

  // Ayrılınan merkez silinmez: kaydı `unassignedAt` damgasıyla kalır
  const handleArchiveCenter = (centerId) => {
    const center = centers.find((c) => c.id === centerId);
    setSelectedCenters(selectedCenters.filter((id) => id !== centerId));
    setArchivedCenters((prev) => (prev.some((c) => c.centerId === centerId)
      ? prev
      : [...prev, { centerId, centerName: center?.Adı, unassignedAt: null }]));
  };

  const handleRestoreCenter = (centerId) => {
    setArchivedCenters((prev) => prev.filter((c) => c.centerId !== centerId));
    setSelectedCenters(selectedCenters.includes(centerId) ? selectedCenters : [...selectedCenters, centerId]);
  };

  const roleSummary = () => {
    if (selectedRole.length === 0) return '';
    if (selectedRole.length === 1) return roleLabel(t, selectedRole[0]);
    return label('rolesSelected', { count: selectedRole.length });
  };
  const centerSummary = () => {
    if (selectedCenters.length === 0) return '';
    if (selectedCenters.length === 1) return centers.find((c) => c.id === selectedCenters[0])?.Adı || '';
    return label('centersSelected', { count: selectedCenters.length });
  };

  // Kayıt için en az bir rol ve (aktif ya da ayrılmış) bir merkez gerekir
  const canSubmit = !loading
    && selectedRole.length > 0
    && (isEdit ? (selectedCenters.length > 0 || archivedCenters.length > 0) : (Boolean(selectedEmployee) && selectedCenters.length > 0));
  const workerName = worker ? `${worker.name || worker.eName || ''} ${worker.surname || worker.eSurname || ''}`.trim() : '';

  return (
    <Modal
      isOpen
      onClose={() => !loading && onClose()}
      size="lg"
      title={isEdit ? label('editTitle') : label('addTitle')}
      subtitle={isEdit ? workerName : undefined}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
          {isEdit ? <LuUserPen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuUserPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading} className="max-sm:tw-hidden">{t('common.close')}</Button>
          <Button variant="primary" onClick={onSubmit} disabled={!canSubmit} className="max-sm:tw-flex-1">
            {loading
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {loading ? label(isEdit ? 'updating' : 'adding') : label(isEdit ? 'update' : 'add')}
          </Button>
        </>
      )}
    >
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        {!isEdit && (
          <Field label={label('employee')} htmlFor="worker-form-employee" required className="sm:tw-col-span-2">
            <SearchSelect
              inputId="worker-form-employee"
              options={employeeOptions}
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              placeholder={label('employeePlaceholder')}
              isClearable
            />
          </Field>
        )}

        <Field label={label('roles')} htmlFor="worker-form-roles" required error={hasManagerWarning ? label('managerWarning') : undefined}>
          <MultiSelect
            id="worker-form-roles"
            options={ROLE_OPTIONS.map((role) => ({ key: role.id, label: roleLabel(t, role.name), name: role.name }))}
            isSelected={(option) => selectedRole.includes(option.name)}
            onToggle={toggleRole}
            summary={roleSummary()}
            placeholder={label('rolesPlaceholder')}
            searchPlaceholder={label('roleSearch')}
            warning={hasManagerWarning}
          />
        </Field>
        <Field label={label('centers')} htmlFor="worker-form-centers" required>
          <MultiSelect
            id="worker-form-centers"
            options={centerOptions.map((option) => ({
              ...option,
              key: option.id,
              label: option.Adı || option.id,
              hint: option.Bilgi || undefined,
              muted: Boolean(option.isDeleted),
              badge: option.isDeleted ? (
                <span className="tw-shrink-0 tw-rounded-full tw-bg-rose-500/10 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-rose-700 dark:tw-text-rose-300">{label('deletedCenter')}</span>
              ) : null,
            }))}
            matches={(option, search) => (option.Adı || '').toLocaleLowerCase('tr-TR').includes(search) || (option.Bilgi || '').toLocaleLowerCase('tr-TR').includes(search)}
            isSelected={(option) => optionMatchesAnyCenterId(option, selectedCenters)}
            onToggle={toggleCenter}
            summary={centerSummary()}
            placeholder={label('centersPlaceholder')}
            searchPlaceholder={label('centerSearch')}
            warning={hasManagerWarning}
          />
        </Field>

        {selectedCenters.length > 0 && (
          <div className="tw-space-y-2 sm:tw-col-span-2">
            <p className="tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('selectedCenters')}</p>
            <div ref={chipsRef} className="tw-flex tw-max-h-32 tw-flex-wrap tw-gap-1.5 tw-overflow-y-auto tw-overscroll-contain glass-scrollbar">
              {selectedCenters.map((centerId) => {
                const center = centers.find((c) => c.id === centerId);
                if (!center) return null;
                return (
                  <span key={centerId} className={`${CHIP} tw-bg-violet-500/10 tw-text-violet-800 dark:tw-text-violet-200`}>
                    <LuBuilding2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                    <span className="tw-truncate">{center.Adı}</span>
                    <button
                      type="button"
                      onClick={() => (isEdit ? handleArchiveCenter(centerId) : setSelectedCenters(selectedCenters.filter((id) => id !== centerId)))}
                      aria-label={isEdit ? label('archiveCenter', { name: center.Adı }) : label('removeCenter', { name: center.Adı })}
                      title={isEdit ? label('archiveCenterHint') : undefined}
                      className="tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors hover:tw-bg-violet-900/10 dark:hover:tw-bg-white/15"
                    >
                      <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
            <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
              <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              {label('fteNote')}
            </p>
          </div>
        )}

        {/* Ayrılınan merkezler: geçmiş kayıtlar, dosyalar ve FTE bilgileri korunur */}
        {isEdit && archivedCenters.length > 0 && (
          <div className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-amber-500/25 tw-bg-amber-500/[0.06] tw-p-3 sm:tw-col-span-2 dark:tw-border-amber-400/20">
            <p className="tw-px-0.5 tw-text-xs tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">{label('archivedCenters')}</p>
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {archivedCenters.map((archived) => {
                const center = centers.find((c) => c.id === archived.centerId);
                const name = center?.Adı || archived.centerName || label('unknownCenter');
                const leftOn = formatLeftDate(archived, locale);
                return (
                  <span key={archived.centerId} className={`${CHIP} tw-bg-white/70 tw-text-slate-700 dark:tw-bg-white/[0.06] dark:tw-text-slate-200`}>
                    <LuBuilding2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                    <span className="tw-truncate">{name}</span>
                    <span className="tw-shrink-0 tw-rounded-full tw-bg-amber-500/15 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-text-amber-800 dark:tw-text-amber-200">
                      {leftOn ? label('leftOn', { date: leftOn }) : label('willLeave')}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRestoreCenter(archived.centerId)}
                      aria-label={label('restoreCenter', { name })}
                      title={label('restoreCenter', { name })}
                      className="tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors hover:tw-bg-slate-900/10 dark:hover:tw-bg-white/15"
                    >
                      <LuRotateCcw aria-hidden="true" className="tw-h-3 tw-w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
            <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-amber-800/80 dark:tw-text-amber-200/80">
              <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              {label('archivedNote')}
            </p>
          </div>
        )}

        <Field label={label('workerType')} htmlFor="worker-form-type">
          <ChoiceChips
            id="worker-form-type"
            options={[{ value: 'primer', label: label('primer') }, { value: 'backup', label: label('backup') }]}
            value={selectedWorkerType}
            onChange={setSelectedWorkerType}
          />
        </Field>
        <Field label={label('blindStatus')} htmlFor="worker-form-blind">
          <ChoiceChips
            id="worker-form-blind"
            options={[{ value: 'blinded', label: label('blinded') }, { value: 'unblinded', label: label('unblinded') }]}
            value={selectedBlindStatus}
            onChange={setSelectedBlindStatus}
          />
        </Field>

        {isEdit && (
          <Field label={label('status')} htmlFor="worker-form-status">
            <SelectInput
              id="worker-form-status"
              value={selectedStatus}
              current={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
              options={[{ value: 'active', label: t('projectManagement.status.active') }, { value: 'passive', label: t('projectManagement.status.inactive') }]}
            />
          </Field>
        )}

        <Field label={label('info')} htmlFor="worker-form-info" className="sm:tw-col-span-2">
          <TextArea id="worker-form-info" rows={2} value={selectedInfo} onChange={(event) => setSelectedInfo(event.target.value)} placeholder={label('infoPlaceholder')} />
        </Field>
      </div>
    </Modal>
  );
};

WorkerFormModal.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  worker: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  availableEmployees: PropTypes.arrayOf(PropTypes.object),
  centers: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedEmployee: PropTypes.string,
  setSelectedEmployee: PropTypes.func,
  selectedRole: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedRole: PropTypes.func.isRequired,
  selectedCenters: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedCenters: PropTypes.func.isRequired,
  archivedCenters: PropTypes.arrayOf(PropTypes.object),
  setArchivedCenters: PropTypes.func,
  selectedInfo: PropTypes.string,
  setSelectedInfo: PropTypes.func.isRequired,
  selectedWorkerType: PropTypes.string,
  setSelectedWorkerType: PropTypes.func.isRequired,
  selectedBlindStatus: PropTypes.string,
  setSelectedBlindStatus: PropTypes.func.isRequired,
  selectedStatus: PropTypes.string,
  setSelectedStatus: PropTypes.func,
  hasManagerWarning: PropTypes.bool,
};

WorkerFormModal.defaultProps = {
  availableEmployees: [],
  archivedCenters: [],
};

export default WorkerFormModal;
